import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as d3 from 'd3';

// Two-ring donut of the exact platform combinations that occur in the data.
// Inner ring = combination of platform families (MS, SomaScan, Olink, MSD),
// one slice per combination present -- a protein sits in exactly one;
// outer ring = SomaScan combinations split by version (7k / unspecified).
// The alternative view, PlatformFilter, has one button per platform instead.

const FAMILY_ORDER = ['MS', 'SomaScan', 'Olink', 'MSD'];

// The slice (outer-ring id) a protein belongs to, from its platformKeys.
export function platformSliceOf(keys) {
  const has = new Set(keys || []);
  const families = FAMILY_ORDER.filter(f => has.has(f));
  if (families.length === 0) return null;
  const combo = families.join(' + ');
  if (!has.has('SomaScan')) return combo;
  return `${combo}|${has.has('SomaScan 7k') ? '7k' : 'unspec'}`;
}

// Fixed colours for the combinations the chart has always had, so they look
// as before; the rest take the next free colour from EXTRA.
const BASE_COLORS = {
  light: { 'MS': '#2a6fc7', 'SomaScan': '#2c8c4a', 'MS + SomaScan': '#7339b8', 'Olink': '#0e7c86', 'MSD': '#b4530f' },
  dark: { 'MS': '#5fa8ff', 'SomaScan': '#5fc977', 'MS + SomaScan': '#a980eb', 'Olink': '#3cc6cf', 'MSD': '#e8955a' },
};
// Chosen to stay apart from the base colours and from each other (no second
// teal next to Olink, no second purple next to MS + SomaScan, and no orange
// before the slots run out, since orange marks a selected slice).
const EXTRA = {
  light: ['#c2185b', '#6d4c41', '#9e9d24', '#e0a800', '#455a64', '#283593', '#d84315', '#558b2f', '#ad1457', '#8d6e63'],
  dark: ['#f06292', '#bcaaa4', '#dce775', '#ffd54f', '#90a4ae', '#9fa8da', '#ff8a65', '#aed581', '#f48fb1', '#d7ccc8'],
};

// Groups (inner ring) and their subs (outer ring) for the combinations present,
// singles first, then pairs, triples, ...; within a size in family order.
function buildGroups(rowData, isDark) {
  const combos = new Set();
  for (const row of rowData || []) {
    const slice = platformSliceOf(row.platformKeys);
    if (slice) combos.add(slice.split('|')[0]);
  }
  const rank = c => c.split(' + ').map(f => FAMILY_ORDER.indexOf(f));
  const ordered = Array.from(combos).sort((a, b) => {
    const ra = rank(a); const rb = rank(b);
    if (ra.length !== rb.length) return ra.length - rb.length;
    for (let k = 0; k < ra.length; k++) if (ra[k] !== rb[k]) return ra[k] - rb[k];
    return 0;
  });
  const mode = isDark ? 'dark' : 'light';
  let extra = 0;
  return ordered.map(combo => {
    const color = BASE_COLORS[mode][combo] || EXTRA[mode][extra++ % EXTRA[mode].length];
    const lighter = d3.color(color).brighter(isDark ? 0.6 : 0.8).formatHex();
    const subs = combo.split(' + ').includes('SomaScan')
      ? [
          { id: `${combo}|unspec`, label: combo.replace('SomaScan', 'SomaScan (unspecified)'), color },
          { id: `${combo}|7k`, label: combo.replace('SomaScan', 'SomaScan 7k'), color: lighter },
        ]
      : [{ id: combo, label: combo, color }];
    return { id: combo, label: combo, color, subs };
  });
}

// Smallest angle a non-empty outer slice is drawn with (~4.5°). A platform that
// holds 2 of 1,000 proteins would otherwise be a hairline nobody can see or
// click. The space comes proportionally out of the larger slices, so the
// drawing is no longer exact for them; counts and percentages stay true.
const MIN_SLICE_ANGLE = 0.08;

// Display angle per subcategory: proportional to its count, but at least
// MIN_SLICE_ANGLE when non-empty. Slices pinned at the minimum are taken out
// and the rest re-proportioned until no remaining slice falls below it.
function displayAngles(subCounts, total) {
  const TWO_PI = Math.PI * 2;
  const ids = Object.keys(subCounts).filter(id => subCounts[id] > 0);
  const angles = Object.fromEntries(Object.keys(subCounts).map(id => [id, 0]));
  if (ids.length * MIN_SLICE_ANGLE >= TWO_PI) {
    ids.forEach(id => { angles[id] = (subCounts[id] / total) * TWO_PI; });
    return angles;
  }
  const pinned = new Set();
  for (;;) {
    const free = ids.filter(id => !pinned.has(id));
    const freeCount = free.reduce((s, id) => s + subCounts[id], 0);
    const room = TWO_PI - pinned.size * MIN_SLICE_ANGLE;
    const tooSmall = free.filter(id => (subCounts[id] / freeCount) * room < MIN_SLICE_ANGLE);
    if (tooSmall.length === 0) {
      free.forEach(id => { angles[id] = (subCounts[id] / freeCount) * room; });
      break;
    }
    tooSmall.forEach(id => pinned.add(id));
  }
  pinned.forEach(id => { angles[id] = MIN_SLICE_ANGLE; });
  return angles;
}

const SELECTED_COLOR = { light: '#fb923c', dark: '#ffe17a' };

export default function PlatformDonutChart({
  rowData,
  selectedPlatforms,
  onToggleSub,
  onToggleGroup,
  isDark,
}) {
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const [width, setWidth] = useState(0);

  const groups = useMemo(() => buildGroups(rowData, isDark), [rowData, isDark]);

  const counts = useMemo(() => {
    const subCounts = {};
    for (const g of groups) {
      for (const s of g.subs) subCounts[s.id] = 0;
    }
    if (Array.isArray(rowData)) {
      for (const row of rowData) {
        const sub = platformSliceOf(row.platformKeys);
        if (sub && Object.prototype.hasOwnProperty.call(subCounts, sub)) {
          subCounts[sub] += 1;
        }
      }
    }
    const groupCounts = {};
    for (const g of groups) {
      groupCounts[g.id] = g.subs.reduce((s, sub) => s + subCounts[sub.id], 0);
    }
    return { sub: subCounts, group: groupCounts };
  }, [rowData, groups]);

  const total = useMemo(
    () => Object.values(counts.group).reduce((s, n) => s + n, 0),
    [counts]
  );

  useEffect(() => {
    if (!containerRef.current) return;
    const el = containerRef.current;
    const update = () => setWidth(el.clientWidth || 0);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    if (!svgRef.current || width <= 0) return;

    const W = width;
    const H = 440;
    const cx = W / 2;
    const cy = H / 2 - 4;
    const labelMargin = 245; // leader (34 px) + labels such as "SomaScan (unspecified) + Olink"
    const maxOuter = Math.max(60, Math.min((W - labelMargin * 2) * 0.5, H * 0.42));
    const innerHole = Math.max(28, maxOuter * 0.28);
    const ringSplit = innerHole + (maxOuter - innerHole) * 0.55;

    const fg = isDark ? '#ddd' : '#222';
    const selectedFill = SELECTED_COLOR[isDark ? 'dark' : 'light'];

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();
    svg.attr('viewBox', `0 0 ${W} ${H}`)
       .attr('width', '100%')
       .attr('height', H);

    if (total === 0) {
      svg.append('text')
        .attr('x', cx).attr('y', cy)
        .attr('text-anchor', 'middle')
        .attr('fill', fg).attr('font-size', 14)
        .text('No platform data');
      return;
    }

    // Build a single list of slice descriptors. Single-sub groups (e.g. MS)
    // collapse into one unified slice that spans the full ring height.
    const angles = displayAngles(counts.sub, total);
    let cursor = -Math.PI / 2; // start at 12 o'clock
    const slices = [];
    for (const group of groups) {
      const groupCount = counts.group[group.id];
      const groupSpan = group.subs.reduce((s, sub) => s + angles[sub.id], 0);
      const groupStart = cursor;
      const groupEnd = cursor + groupSpan;

      if (group.subs.length === 1) {
        const sub = group.subs[0];
        const isSelected = selectedPlatforms.has(sub.id);
        slices.push({
          kind: 'unified',
          label: group.label,
          count: groupCount,
          startAngle: groupStart,
          endAngle: groupEnd,
          innerR: innerHole,
          outerR: maxOuter,
          baseColor: group.color,
          isSelected,
          onClick: () => onToggleSub(sub.id, !isSelected),
        });
      } else {
        const subIds = group.subs.map(s => s.id);
        const nonEmptySubIds = subIds.filter(id => counts.sub[id] > 0);
        const groupSelected = nonEmptySubIds.length > 0
          && nonEmptySubIds.every(id => selectedPlatforms.has(id));
        slices.push({
          kind: 'parent',
          label: group.label,
          count: groupCount,
          startAngle: groupStart,
          endAngle: groupEnd,
          innerR: innerHole,
          outerR: ringSplit,
          baseColor: group.color,
          isSelected: groupSelected,
          onClick: () => onToggleGroup(group.id),
        });

        let subCursor = groupStart;
        for (const sub of group.subs) {
          const subCount = counts.sub[sub.id];
          const subSpan = angles[sub.id];
          const isSelected = selectedPlatforms.has(sub.id);
          slices.push({
            kind: 'sub',
            label: sub.label,
            count: subCount,
            startAngle: subCursor,
            endAngle: subCursor + subSpan,
            innerR: ringSplit + 1,
            outerR: maxOuter,
            baseColor: sub.color,
            isSelected,
            onClick: () => onToggleSub(sub.id, !isSelected),
          });
          subCursor += subSpan;
        }
      }
      cursor = groupEnd;
    }

    const visible = slices.filter(s =>
      (s.endAngle - s.startAngle) > 0 && s.outerR > s.innerR
    );

    const arcGen = d3.arc().padAngle(0.005).cornerRadius(2);
    const g = svg.append('g').attr('transform', `translate(${cx},${cy})`);

    // Slices
    const sliceSel = g.selectAll('path.platform-slice')
      .data(visible)
      .join('path')
      .attr('class', 'platform-slice')
      .attr('d', d => arcGen
        .innerRadius(d.innerR)
        .outerRadius(d.outerR)({ startAngle: d.startAngle, endAngle: d.endAngle }))
      .attr('fill', d => d.isSelected ? selectedFill : d.baseColor)
      .attr('stroke', d => d.isSelected ? fg : (isDark ? '#222' : '#fff'))
      .attr('stroke-width', d => d.isSelected ? 2 : 1)
      .style('cursor', d => d.count > 0 ? 'pointer' : 'default')
      .on('mouseover', function (_e, d) {
        if (d.count > 0 && !d.isSelected) {
          d3.select(this).attr('fill', d3.color(d.baseColor).brighter(0.3).formatHex());
        }
      })
      .on('mouseout', function (_e, d) {
        if (!d.isSelected) d3.select(this).attr('fill', d.baseColor);
      })
      .on('click', (_e, d) => { if (d.count > 0) d.onClick(); });

    sliceSel.append('title').text(d => {
      const pct = (d.count / total * 100).toFixed(1);
      const action = d.isSelected ? 'remove' : 'apply';
      return `${d.label}\n${d.count} proteins (${pct}%)\nClick to ${action} filter`;
    });

    // Inline count labels (numbers on top of slices)
    g.selectAll('text.platform-count-label')
      .data(visible)
      .join('text')
      .attr('class', 'platform-count-label')
      .attr('text-anchor', 'middle')
      .attr('fill', '#fff')
      .style('pointer-events', 'none')
      .style('paint-order', 'stroke')
      .style('stroke', 'rgba(0,0,0,0.45)')
      .style('stroke-width', '2px')
      .each(function (d) {
        const node = d3.select(this);
        node.text('');
        const angle = d.endAngle - d.startAngle;
        const minAngle = d.kind === 'sub' ? 0.18 : 0.22;
        if (d.count <= 0 || angle < minAngle) return;
        const midR = (d.innerR + d.outerR) / 2;
        const midA = (d.startAngle + d.endAngle) / 2;
        const lx = midR * Math.sin(midA);
        const ly = -midR * Math.cos(midA);
        node.attr('transform', `translate(${lx},${ly})`);
        const showPercent = d.kind !== 'sub' && (d.outerR - d.innerR) > 35;
        if (showPercent) {
          const pct = (d.count / total * 100).toFixed(0);
          node.append('tspan')
            .attr('x', 0).attr('dy', '-0.1em')
            .attr('font-size', 12).attr('font-weight', 700)
            .text(`${d.count}`);
          node.append('tspan')
            .attr('x', 0).attr('dy', '1.15em')
            .attr('font-size', 10).attr('font-weight', 500)
            .text(`${pct}%`);
        } else {
          node.append('tspan')
            .attr('x', 0).attr('dy', '0.32em')
            .attr('font-size', 11).attr('font-weight', 700)
            .text(`${d.count}`);
        }
      });

    // Outer leader-line labels with the platform name. Only for slices that
    // touch the donut edge (unified slices and outer-ring sub slices).
    const labelSlices = visible.filter(s =>
      s.count > 0 && s.outerR === maxOuter && s.kind !== 'parent'
    );
    // Leader-line geometry per label; then, per side, labels sorted top to
    // bottom are pushed apart so neighbouring small slices stay readable.
    const LABEL_GAP = 15;
    const labels = labelSlices.map(s => {
      const midA = (s.startAngle + s.endAngle) / 2;
      const sinA = Math.sin(midA);
      const cosA = Math.cos(midA);
      const r1 = maxOuter + 4;
      const r2 = maxOuter + 22;
      return {
        s,
        isRight: sinA >= 0,
        x1: r1 * sinA, y1: -r1 * cosA,
        x2: r2 * sinA, y2: -r2 * cosA,
        y: -r2 * cosA,
      };
    });
    for (const side of [true, false]) {
      const col = labels.filter(l => l.isRight === side).sort((a, b) => a.y - b.y);
      for (let k = 1; k < col.length; k++) {
        if (col[k].y - col[k - 1].y < LABEL_GAP) col[k].y = col[k - 1].y + LABEL_GAP;
      }
      // Keep the column inside the chart: shift back up if it ran off the bottom.
      const overflow = col.length ? col[col.length - 1].y - (H / 2 - 8) : 0;
      if (overflow > 0) col.forEach(l => { l.y -= overflow; });
    }
    for (const l of labels) {
      const { s, isRight } = l;
      // Every label on a side ends its leader at the same x, so the names
      // form a straight column instead of following the curve.
      const x3 = isRight ? maxOuter + 30 : -(maxOuter + 30);

      g.append('polyline')
        .attr('points', `${l.x1},${l.y1} ${l.x2},${l.y2} ${x3},${l.y}`)
        .attr('stroke', fg)
        .attr('stroke-opacity', 0.5)
        .attr('fill', 'none')
        .style('pointer-events', 'none');

      g.append('text')
        .attr('x', x3 + (isRight ? 4 : -4))
        .attr('y', l.y)
        .attr('dy', '0.32em')
        .attr('text-anchor', isRight ? 'start' : 'end')
        .attr('fill', fg)
        .attr('font-size', 12)
        .attr('font-weight', s.isSelected ? 700 : 500)
        .style('pointer-events', 'none')
        .text(s.label);
    }

    // Center total
    const center = g.append('g').style('pointer-events', 'none');
    center.append('text')
      .attr('text-anchor', 'middle').attr('y', -2)
      .attr('fill', fg).attr('font-size', 18).attr('font-weight', 700)
      .text(`${total}`);
    center.append('text')
      .attr('text-anchor', 'middle').attr('y', 14)
      .attr('fill', fg).attr('font-size', 10).attr('opacity', 0.75)
      .text('proteins');
  }, [groups, counts, total, width, isDark, selectedPlatforms, onToggleSub, onToggleGroup]);

  const selectedColor = SELECTED_COLOR[isDark ? 'dark' : 'light'];

  const cardStyle = {
    display: 'flex',
    flexDirection: 'column',
    padding: '6px 10px',
    border: `1px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)'}`,
    borderRadius: 6,
  };

  // One legend card: the combination's own toggle, then its version split.
  const legendEntry = (group) => {
    const groupCount = counts.group[group.id];
    const subIds = group.subs.map(s => s.id);
    const nonEmpty = subIds.filter(id => counts.sub[id] > 0);
    const allSelected = nonEmpty.length > 0 && nonEmpty.every(id => selectedPlatforms.has(id));
    const groupColor = allSelected ? selectedColor : group.color;
    return (
      <div
        key={group.id}
        style={{ ...cardStyle, gap: 4 }}
      >
        <button
          type="button"
          onClick={() => groupCount > 0 && onToggleGroup(group.id)}
          disabled={groupCount === 0}
          style={{
            display: 'flex',
            width: '100%',
            alignItems: 'center',
            gap: 8,
            background: 'transparent',
            border: 'none',
            padding: 0,
            color: 'inherit',
            cursor: groupCount > 0 ? 'pointer' : 'default',
            fontSize: '0.92rem',
            fontWeight: allSelected ? 700 : 600,
            textAlign: 'left',
            opacity: groupCount === 0 ? 0.5 : 1,
          }}
          title={groupCount > 0
            ? `Click to ${allSelected ? 'remove' : 'apply'} the ${group.label} group filter`
            : 'No proteins in this group'}
        >
          <span style={{
            display: 'inline-block',
            flex: 'none',
            width: 14,
            height: 14,
            borderRadius: 3,
            background: groupColor,
            border: allSelected ? `1.5px solid ${isDark ? '#ddd' : '#222'}` : 'none',
          }} />
          <span style={{ flex: 1 }}>{group.label}</span>
          <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{groupCount}</strong>
        </button>
        {group.subs.length > 1 && group.subs.map(sub => {
          const subCount = counts.sub[sub.id];
          if (subCount === 0) return null;
          const isSelected = selectedPlatforms.has(sub.id);
          const subColor = isSelected ? selectedColor : sub.color;
          return (
            <button
              key={sub.id}
              type="button"
              onClick={() => subCount > 0 && onToggleSub(sub.id, !isSelected)}
              disabled={subCount === 0}
              style={{
                display: 'flex',
                width: '100%',
                alignItems: 'center',
                gap: 8,
                background: 'transparent',
                border: 'none',
                padding: '0 0 0 14px',
                color: 'inherit',
                cursor: subCount > 0 ? 'pointer' : 'default',
                fontSize: '0.85rem',
                fontWeight: isSelected ? 700 : 400,
                textAlign: 'left',
                opacity: subCount === 0 ? 0.5 : 1,
              }}
              title={subCount > 0
                ? `Click to ${isSelected ? 'remove' : 'apply'} ${sub.label}`
                : 'No proteins in this subcategory'}
            >
              <span style={{
                display: 'inline-block',
                flex: 'none',
                width: 11,
                height: 11,
                borderRadius: 2,
                background: subColor,
                border: isSelected ? `1.5px solid ${isDark ? '#ddd' : '#222'}` : 'none',
              }} />
              <span style={{ flex: 1 }}>{sub.label}</span>
              <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{subCount}</strong>
            </button>
          );
        })}
      </div>
    );
  };

  // Combinations without a version split are one-line cards; they stack in one
  // narrow column instead of each taking a full-width cell in the grid.
  const singles = groups.filter(g => g.subs.length === 1);
  const splits = groups.filter(g => g.subs.length > 1);

  return (
    <div ref={containerRef} style={{ width: '100%' }}>
      <svg ref={svgRef} style={{ display: 'block' }} />
      <div style={{ textAlign: 'center', fontSize: '0.8rem', opacity: 0.75, marginTop: 4 }}>
        Click an inner-ring slice to toggle a whole group; click an outer slice for a specific subcategory.
      </div>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'flex-start',
          gap: '10px 14px',
          marginTop: 10,
        }}
        role="group"
        aria-label="Platform legend"
      >
        {singles.length > 0 && (
          <div style={{ flex: '0 0 210px', display: 'flex', flexDirection: 'column', gap: 10 }}>
            {singles.map(g => legendEntry(g))}
          </div>
        )}
        {/* Equal columns, so the cards line up in rows and columns however
            many combinations there are. */}
        <div
          style={{
            flex: '1 1 480px',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
            alignItems: 'stretch',
            gap: '10px 14px',
          }}
        >
          {splits.map(g => legendEntry(g))}
        </div>
      </div>
    </div>
  );
}
